/*
 * Copyright (c) 2025 - 2026. Bluemate B.V.
 */
import './style/buyBoxNotifications.scss';

import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {BuyBoxEvent, NotificationType} from '../../../util/enums';

import {PortalSelectOption, PortalSelectValue} from '../../../util/types';

import Notification from '../../../models/notificationModel';

import {AwesomeIcon, Link, showSnackbar} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import {BluemateBadge} from '../../controls';

import {NotificationProps, NotificationTypeProps} from '../notificationsPage';

import useDefaultNotification from '../useDefaultNotification';

import NotificationProducts from '../notificationComponents/notificationProducts';

import NotificationActions from '../notificationComponents/notificationActions';

import NotificationChannelsSelect from '../notificationComponents/notificationChannelsSelect';

import NotificationChannelsBadge from '../notificationComponents/notificationChannelsBadge';

import BluemateBadgePremium from '../bluemateBadgePremium';

function BuyBoxNotification(props: NotificationProps) {
    const {t} = useTranslation();
    const {
        notification,
        loading,
        onChangeNotification,
        onDeleteNotification,
        onSendTestNotification,
        disabled,
        canEdit,
        canDelete,
        onReplaceNotification
    } = props;
    const [onChangeEmailAddresses] = useDefaultNotification({...props});
    // Bumped to force a re-render with a fresh (unchanged) value array, which
    // the controlled PortalSelect syncs back into its internal state — this is
    // how a REJECTED change (empty selection) visually snaps back.
    const [, setSyncNonce] = useState(0);

    const eventItems: Array<PortalSelectOption<BuyBoxEvent>> = useMemo(
        () => [
            {value: BuyBoxEvent.LOST, label: t('notifications.buyBox.eventLost', 'Koopblok verloren')},
            {value: BuyBoxEvent.GOT, label: t('notifications.buyBox.eventGot', 'Koopblok verkregen')}
        ],
        [t]
    );

    function parseEvents(value: PortalSelectValue<BuyBoxEvent>): string {
        const selected = Array.isArray(value) ? value : [];
        if (selected.length === 1)
            return selected[0] === BuyBoxEvent.LOST
                ? t('notifications.buyBox.eventLost', 'Koopblok verloren')
                : t('notifications.buyBox.eventGot', 'Koopblok verkregen');
        return t('notifications.buyBox.eventChange', 'Koopblok verandering');
    }

    function onChangeEvents(value: BuyBoxEvent | null, _selected: boolean, values?: Array<BuyBoxEvent>) {
        let next = values ?? [];
        // The selection may never end up empty. The "Wis"/Clear header control
        // (value === null) collapses to the minimum allowed selection — one
        // event; unchecking the last individual event is rejected instead.
        if (!next.length) {
            if (value === null) {
                next = [BuyBoxEvent.LOST];
            } else {
                showSnackbar(t('notifications.buyBox.minOneEvent', 'Kies minimaal één gebeurtenis'), {
                    backgroundColor: 'color-blue-bluemate',
                    textColor: 'white'
                });
                setSyncNonce(nonce => nonce + 1);
                return;
            }
        }
        const config = notification.buyBoxConfig;
        config.buy_box_events = new Set(next);
        notification.flex = config.dump();
        onChangeNotification(notification);
        // Re-render with the persisted value so the controlled PortalSelect
        // syncs its internal state (Clear left it empty).
        setSyncNonce(nonce => nonce + 1);
    }

    function renderTitle() {
        return (
            <div className='notification-title beta'>
                <AwesomeIcon name='trophy' className='type-icon' />{' '}
                {t('notifications.buyBox.title', 'Koopblok notificatie')}{' '}
                <NotificationChannelsBadge notification={notification} /> <BluemateBadgePremium /> {renderTip()}
            </div>
        );
    }

    function renderTip() {
        // The pill is a BluemateBadge; the rich hover info (multi-line + link)
        // goes through the portal tooltip so it stays inside the viewport, in
        // interactive mode so the link remains clickable.
        return (
            <BluemateBadge
                color='blue'
                className='tip-badge'
                tooltipClassName='beta-info'
                tooltipInteractive
                label={
                    <>
                        <AwesomeIcon name='lightbulb' /> {t('notifications.buyBox.tipLabel', 'Tip')}
                    </>
                }
                tooltip={
                    <>
                        {t('notifications.buyBox.tipLine1', 'Krijg je veel koopblok-notificaties?')}
                        <br />
                        {t(
                            'notifications.buyBox.tipLine2',
                            'Dat komt vaak door hoge concurrentie, automatische repricers en veranderende leverbeloftes in het weekend.'
                        )}
                        <br />
                        {t(
                            'notifications.buyBox.tipLine3',
                            'Wil je dat deze meldingen niet meer in je algemene inbox terechtkomen?'
                        )}
                        <br />
                        <Link
                            route='https://support.bluemate.nl/article/54-ik-krijg-erg-veel-bol-notificaties-binnen-wat-moet-ik-doen'
                            target='_blank'
                        >
                            {t(
                                'notifications.buyBox.tipLinkText',
                                '👉 Lees hier hoe je ze automatisch in een aparte map ontvangt in Gmail of Outlook.'
                            )}
                        </Link>
                    </>
                }
            />
        );
    }

    return (
        <div className={classNames('buy-box-notification notification-config', disabled && 'disabled')}>
            {renderTitle()}
            <div className='notification-card'>
                <div className='notification'>
                    <section>
                        <label className='fixed-text'>{t('notifications.buyBox.atLabel', 'Bij')}</label>
                        <PortalSelectController
                            value={Array.from(notification.buyBoxConfig.buy_box_events)}
                            onChange={onChangeEvents}
                            items={eventItems}
                            parseSelectValue={parseEvents}
                            disabled={loading || disabled || !canEdit}
                            multiple
                            controls={['selectAll', 'deselectAll']}
                            size='medium'
                            outlined
                        />
                    </section>
                    <NotificationProducts
                        onSuccess={onReplaceNotification}
                        notification={notification}
                        disabled={loading || disabled || !canEdit}
                    />
                    <NotificationChannelsSelect
                        notification={notification}
                        disabled={loading || disabled || !canEdit}
                        onChangeEmailAddresses={onChangeEmailAddresses}
                        onReplaceNotification={onReplaceNotification}
                    />
                </div>
                <NotificationActions
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    disabled={disabled}
                    notification={notification}
                    canDelete={canDelete}
                />
            </div>
        </div>
    );
}

export default function BuyBoxNotifications(props: NotificationTypeProps) {
    const {notifications, moduleActive} = props;
    const buyBoxNotifications = useMemo(() => {
        return notifications.filter(
            (notification: Notification) => notification.notificationType === NotificationType.BUY_BOX
        );
    }, [notifications]);
    if (!buyBoxNotifications.length) return null;

    function renderNotification(notification: Notification) {
        return (
            <BuyBoxNotification key={notification.id} notification={notification} disabled={!moduleActive} {...props} />
        );
    }

    return (
        <div className='notification-type buy-box-notifications'>
            <div className='notifications'>{buyBoxNotifications.map(renderNotification)}</div>
        </div>
    );
}
